import UPSwitch from 'components/switch';
import { UPTanStackTable, UPTanStackTableColumn } from 'components/tanstack-table';
import { MfaPlatform } from 'constants/MfaPlatform';
import { MfaRole } from 'constants/MfaRole';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SECURITY__MFA_NOT_REQUIRED,
  SECURITY__MFA_REQUIRED,
  SECURITY__PLATFORM_NATIVE_MOBILE,
  SECURITY__PLATFORM_WEBAPP,
  SECURITY__ROLE,
  SECURITY__ROLE_EMPLOYEE,
  SECURITY__ROLE_MANAGER,
  SECURITY__ROLE_SUPERADMIN,
} from 'translations/constants';
import { IAgencyMfaCell } from 'types/mfa/IAgencyMfaPolicy';
import { findCell } from './helpers';
import { CellControl, CellStateLabel, MatrixContainer, RoleLabel } from './styles';

/**
 * Display definition of the matrix. `testId` is the business identity used to build a stable
 * data-testid — never an array index. The wire value and the label are deliberately separate:
 * the API says `Web` where the UI says "Webapp".
 */
const MFA_ROLES = [
  { role: MfaRole.EMPLOYEE, labelKey: SECURITY__ROLE_EMPLOYEE, testId: 'employee' },
  { role: MfaRole.MANAGER, labelKey: SECURITY__ROLE_MANAGER, testId: 'manager' },
  { role: MfaRole.SUPER_ADMIN, labelKey: SECURITY__ROLE_SUPERADMIN, testId: 'superadmin' },
];

const MFA_PLATFORMS = [
  { platform: MfaPlatform.WEB, labelKey: SECURITY__PLATFORM_WEBAPP, testId: 'web' },
  { platform: MfaPlatform.NATIVE_MOBILE, labelKey: SECURITY__PLATFORM_NATIVE_MOBILE, testId: 'native-mobile' },
];

interface MfaMatrixProps {
  cells: IAgencyMfaCell[];
  onChange: (role: MfaRole, platform: MfaPlatform, isRequired: boolean) => void;
}

type MfaMatrixRow = { id: MfaRole; labelKey: string; testId: string } & Record<MfaPlatform, boolean>;

const getRowId = (row: MfaMatrixRow): string => row.id;

const buildMfaMatrixColumns = (
  t: TFunction,
  onChange: MfaMatrixProps['onChange'],
): UPTanStackTableColumn<MfaMatrixRow>[] => [
  {
    id: 'role',
    header: t(SECURITY__ROLE),
    cell: ({ row }) => <RoleLabel>{t(row.labelKey)}</RoleLabel>,
  },
  ...MFA_PLATFORMS.map(
    (platform): UPTanStackTableColumn<MfaMatrixRow> => ({
      id: platform.platform,
      header: t(platform.labelKey),
      cell: ({ row }) => {
        const isRequired = row[platform.platform];
        return (
          <CellControl>
            <UPSwitch
              checked={isRequired}
              onChange={() => onChange(row.id, platform.platform, !isRequired)}
              testId={`mfa-matrix-cell-${row.testId}-${platform.testId}`}
            />
            <CellStateLabel isRequired={isRequired}>
              {t(isRequired ? SECURITY__MFA_REQUIRED : SECURITY__MFA_NOT_REQUIRED)}
            </CellStateLabel>
          </CellControl>
        );
      },
    }),
  ),
];

const MfaMatrix = (props: MfaMatrixProps): JSX.Element => {
  const { cells, onChange } = props;
  const [t] = useTranslation();

  const rows = useMemo(
    () =>
      MFA_ROLES.map(role =>
        MFA_PLATFORMS.reduce(
          (row, platform) => ({
            ...row,
            [platform.platform]: !!findCell(cells, role.role, platform.platform)?.isRequired,
          }),
          { id: role.role, labelKey: role.labelKey, testId: role.testId } as MfaMatrixRow,
        ),
      ),
    [cells],
  );

  const columns = useMemo(() => buildMfaMatrixColumns(t, onChange), [onChange, t]);

  return (
    <MatrixContainer>
      <UPTanStackTable
        data={rows}
        columns={columns}
        getRowId={getRowId}
        enableSorting={false}
        showPaginationControls={false}
        withCheckboxes={false}
        disableSelectionOnClick
      />
    </MatrixContainer>
  );
};

export default MfaMatrix;
